<template lang="pug">
UpsertProduct(
  :forEditorModel="forEditorModel"
  :product="product"
  :disabled="isSaving"
  :isSaving="isSaving"
  :priceIncludesTaxDefaultValue="priceIncludesTaxDefaultValue"
  :enableProductDurationPerParticipant="enableProductDurationPerParticipant"
  :showAccountingFields="showAccountingFields"
  :canViewAuditTrail="canViewAuditTrail"
  :vuelidate="$v"
  @save="validateAndSave"
  @cancel="$emit('cancel')"
  @schedulingBehaviorSelected="setSchedulingBehavior"
  :enableMultilanguage="enableMultilanguage"
  )
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { find, propEq, equals, clone } from 'ramda'
import { mapGetters } from 'vuex'
import UpsertProduct from './UpsertProduct.vue'
import { validateProps } from '@v/util/propValidators.js'
import completeTranslationInfo from '@v/util/completeTranslationInfo.js'
import {
  customFieldDefinitionsToValidation,
  integrationFieldDefinitionsToValidation,
  productDurationFieldsValidation,
  externalProductIdsValidation,
  segmentPriceValidation,
  minMaxPriceValidation,
  resourceIsActive,
} from './upsertProductValidations.js'
import { getAuxiliaryValidationError } from '@v/inventory/products/edit/upsertProductValidations.js'

export default {
  name: 'UpsertProductValidator',
  components: { UpsertProduct },
  props: {
    forEditorModel: {
      type: Object,
      required: true,
      validator: validateProps([
        'Product',
        'CustomFieldDefinitions',
        'ResourceSegments',
        'SegmentlessProduct',
        'RelatedProductGroups',
        'BusinessUnitTaxes',
        'LedgerAccounts',
        'FeatureFlags',
        'ProductFeeds',
        'Resources',
      ]),
    },
    priceIncludesTaxDefaultValue: Boolean,
    enableProductDurationPerParticipant: Boolean,
    showAccountingFields: Boolean,
    canViewAuditTrail: Boolean,
    enableMultilanguage: Boolean,
    isSaving: Boolean,
  },
  data() {
    return {
      product: null,
      schedulingBehavior: null,
      initialDurationConfig: null,
    }
  },
  validations() {
    return {
      product: {
        NameInternal: { required },
        Price: { required },
        TaxKey: { required },
        NameExternal: { required },
        ResourceKey: { required, valid: resourceIsActive(this.selectedResource) },
        ...externalProductIdsValidation(
          this.selectedResource?.ExternalType,
          this.forEditorModel.FeatureFlags,
        ),
        ...customFieldDefinitionsToValidation(this.forEditorModel.CustomFieldDefinitions),
        ...integrationFieldDefinitionsToValidation(
          this.forEditorModel.IntegrationFieldDefinitionsList,
        ),
        ProductDurationConfig: {
          ...productDurationFieldsValidation(
            this.schedulingBehavior,
            this.selectedResource,
            this.product,
          ),
        },
        ActiveSegments: {
          $each: {
            PriceOffset: {
              segmentPriceValidation: this.enableSegmentPricing
                ? segmentPriceValidation(this.product)
                : true,
            },
          },
        },
        MinimumPrice: { valid: minMaxPriceValidation(this.product) },
        MaximumPrice: { valid: minMaxPriceValidation(this.product) },
      },
    }
  },
  computed: {
    ...mapGetters('briqState', ['enabledFeatures']),
    enableSegmentPricing() {
      return this.enabledFeatures.includes('SegmentPricing')
    },
    resources() {
      return this.forEditorModel.Resources
    },
    selectedResource() {
      return find(propEq('Key', this.product.ResourceKey))(this.resources)
    },
    hasMultipleLanguages() {
      return (
        this.enableMultilanguage &&
        !!this.forEditorModel.Product?.TranslationInfo?.AvailableCultures?.length
      )
    },
    availableCultures() {
      const defaultCulture = this.forEditorModel.Product?.TranslationInfo?.DefaultCulture
      return (
        this.forEditorModel.Product?.TranslationInfo?.AvailableCultures?.filter(
          c => c.Name !== defaultCulture.Name,
        ) || null
      )
    },
  },
  methods: {
    validateAndSave(product) {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$notifyError('Please fill out all required fields')
        return
      }
      const validation = getAuxiliaryValidationError(
        product,
        this.forEditorModel,
        this.initialDurationConfig,
      )
      if (validation) {
        this.$notifyError(validation.userMessage)
        if (validation.errorMessage) this.$logError(validation.errorMessage)
      } else this.$emit('save', product)
    },
    setSchedulingBehavior(value) {
      this.schedulingBehavior = value
    },
  },
  beforeMount() {
    this.product = clone(this.forEditorModel.Product)
    this.initialDurationConfig = clone(this.forEditorModel.Product.ProductDurationConfig)
    if (equals(this.product.ActiveSegments, {}) || equals(this.product.ActiveSegments, []))
      this.product.ActiveSegments = null
    if (this.hasMultipleLanguages) {
      this.activeCulture = this.availableCultures[0]
      completeTranslationInfo(this.product)
    }
  },
  watch: {
    // for storybook
    forEditorModel(newModel) {
      this.product = clone(newModel.Product)
      if (this.hasMultipleLanguages) {
        this.activeCulture = this.availableCultures[0]
        completeTranslationInfo(this.product)
      }
    },
  },
}
</script>
